"use client";

import { Fragment, useRef, useState } from "react";
import { CalendarRange } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { ScheduledTask } from "@/lib/study-plan-state";
import TaskCard, { type MoveTarget, type TaskCardActions } from "./TaskCard";
import {
  computeDropOrder,
  DragClone,
  dropInsertIndex,
  dropTargetFrozen,
  freezeColumns,
  InsertionLine,
  isUnchangedColumnOrder,
  pointerXY,
  useFlipReorder,
  type DropIndicator,
  type FrozenColumns,
} from "./schedule-dnd";
import { formatMonthDayWeekday } from "./schedule-shared";

/**
 * 일자 상세 모달 (JCG-2310) — 월 뷰에서 날짜를 누르면 그 하루만 띄운다.
 *
 * 주 뷰(5일)와 동일하게 체크박스 완료·"⋯" 메뉴·드래그앤드롭 순서 변경을 지원한다. 단일 칼럼이라
 * 드롭은 항상 이 날(dateISO) 안에서의 재정렬(날짜 이동 없음, sort_order 만 갱신). DnD 는 주 뷰와
 * 같은 좌표 기반 헬퍼(schedule-dnd: dropTargetFrozen/computeDropOrder/InsertionLine)를 공유한다.
 */
export default function DayDetailDialog({
  dateISO,
  tasks,
  moveTargets,
  actions,
  onReorder,
  readOnly,
  onClose,
  onViewWeek,
}: {
  /** 열린 날짜(null 이면 닫힘). */
  dateISO: string | null;
  /** 그 날의 task(effectiveDate=dateISO, 부모가 정렬해 전달). */
  tasks: ScheduledTask[];
  moveTargets: MoveTarget[];
  actions: TaskCardActions;
  /** 같은 날 안 재정렬(낙관적). movedToDate 는 그대로, sortOrder 만 갱신. */
  onReorder: (
    task: ScheduledTask,
    movedToDate: string | null,
    sortOrder: number | null,
  ) => void;
  /** 이전 회차 조회 모드. 모달 탐색은 유지하고 체크·메뉴·재정렬을 제거한다. */
  readOnly: boolean;
  onClose: () => void;
  /** 이 날이 속한 주의 week 뷰로 전환. */
  onViewWeek: (dateISO: string) => void;
}) {
  return (
    <Dialog
      open={dateISO !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex max-h-[85vh] max-w-md flex-col gap-0 overflow-hidden p-0">
        {dateISO !== null && (
          <DayDialogBody
            dateISO={dateISO}
            tasks={tasks}
            moveTargets={moveTargets}
            actions={actions}
            onReorder={onReorder}
            readOnly={readOnly}
            onViewWeek={onViewWeek}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function DayDialogBody({
  dateISO,
  tasks,
  moveTargets,
  actions,
  onReorder,
  readOnly,
  onViewWeek,
}: {
  dateISO: string;
  tasks: ScheduledTask[];
  moveTargets: MoveTarget[];
  actions: TaskCardActions;
  onReorder: (
    task: ScheduledTask,
    movedToDate: string | null,
    sortOrder: number | null,
  ) => void;
  readOnly: boolean;
  onViewWeek: (dateISO: string) => void;
}) {
  const [dropIndicator, setDropIndicator] = useState<DropIndicator | null>(
    null,
  );
  // 드래그 중인 카드 — 떠 있는 클론을 body 로 portal(e.delta 로 직접 위치). rect=시작 위치/폭.
  const [drag, setDrag] = useState<{
    task: ScheduledTask;
    rect: { left: number; top: number; width: number };
  } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  // 드래그 시작 시 카드 위치 스냅샷(jitter 방지 — 삽입 위치를 고정값으로 계산).
  const frozenRef = useRef<FrozenColumns>(new Map());
  // 재정렬 슬라이드(FLIP) 컨테이너 + 직전 드롭 카드(슬라이드 제외 → 떨어뜨린 자리에 안착).
  const listRef = useRef<HTMLDivElement>(null);
  const lastDraggedRef = useRef<string | null>(null);
  useFlipReorder(listRef, tasks.map((t) => t.uid).join(","), {
    excludeUidRef: lastDraggedRef,
  });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragStart = (e: DragStartEvent) => {
    frozenRef.current = freezeColumns();
    lastDraggedRef.current = null; // FLIP 제외 대상 초기화(직전 드롭 잔재 제거)
    const task = (e.active.data.current?.task as ScheduledTask) ?? null;
    const r = task
      ? document
          .querySelector(`[data-task-uid="${task.uid}"]`)
          ?.getBoundingClientRect()
      : undefined;
    setDrag(
      task && r
        ? { task, rect: { left: r.left, top: r.top, width: r.width } }
        : null,
    );
  };
  const handleDragMove = (e: DragMoveEvent) => {
    if (overlayRef.current) {
      overlayRef.current.style.transform = `translate3d(${e.delta.x}px, ${e.delta.y}px, 0)`;
    }
    setDropIndicator(
      dropTargetFrozen(
        pointerXY(e.activatorEvent, e.delta),
        frozenRef.current,
        String(e.active.id),
      ),
    );
  };
  const handleDragEnd = (e: DragEndEvent) => {
    setDropIndicator(null);
    setDrag(null);
    const task = e.active.data.current?.task as ScheduledTask | undefined;
    if (!task) return;
    const target = dropTargetFrozen(
      pointerXY(e.activatorEvent, e.delta),
      frozenRef.current,
      String(e.active.id),
    );
    if (!target || target.toISO !== dateISO) return; // 이 날 칼럼 밖 드롭 무시
    const siblings = tasks.filter((t) => t.uid !== task.uid);
    const newSortOrder = computeDropOrder(target, siblings, task);
    if (isUnchangedColumnOrder(task, newSortOrder, tasks)) return; // 순서 변화 없으면 no-op
    lastDraggedRef.current = task.uid; // 이 카드는 FLIP 슬라이드 제외(드롭 지점 안착)
    onReorder(task, task.movedToDate, newSortOrder); // 같은 날이라 movedToDate 유지
  };

  const insertIndex = readOnly
    ? null
    : dropInsertIndex(tasks, dropIndicator, dateISO);
  const trackable = tasks.filter((t) => !t.skipped);
  const doneCount = trackable.filter((t) => t.done).length;

  return (
    <>
      <DialogHeader className="shrink-0 border-b px-4 py-3 text-left">
        <DialogTitle className="text-base">
          {formatMonthDayWeekday(dateISO)}
        </DialogTitle>
        <DialogDescription className="text-xs">
          {trackable.length > 0
            ? readOnly
              ? `학습 ${trackable.length}개 · ${doneCount} 완료 — 이전 회차 기록`
              : `학습 ${trackable.length}개 · ${doneCount} 완료 — 끌어서 순서 변경, 체크로 완료`
            : "이 날은 예정된 학습이 없어요."}
        </DialogDescription>
      </DialogHeader>

      {tasks.length > 0 ? (
        readOnly ? (
          <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto p-3">
            {tasks.map((task) => (
              <TaskCard
                key={task.uid}
                task={task}
                actions={actions}
                moveTargets={moveTargets}
                inline
                readOnly
              />
            ))}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            // 스크롤 컨테이너에서 오토스크롤 시 커서 좌표(clientX+delta)가 어긋나 드롭이 빗나갈 수 있어 끔.
            // 긴 날은 사용자가 직접 스크롤 후 보이는 범위에서 재정렬한다.
            autoScroll={false}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            onDragCancel={() => {
              setDropIndicator(null);
              setDrag(null);
            }}
          >
            {/* data-day-column: dropTargetFrozen 이 이 칼럼(=이 날)을 찾는 마커. 본문만 스크롤.
                flex 컨테이너로 두면 카드가 flex-shrink 로 찌부되므로 블록+space-y 로 자연 높이를 유지. */}
            <div
              ref={listRef}
              data-day-column={dateISO}
              className="min-h-0 flex-1 space-y-1.5 overflow-y-auto p-3"
            >
              {tasks.map((task, i) => (
                <Fragment key={task.uid}>
                  {insertIndex === i && <InsertionLine />}
                  {/* data-flip-uid: 재정렬 시 이 래퍼가 새 위치로 미끄러진다(FLIP). */}
                  <div data-flip-uid={task.uid}>
                    <TaskCard
                      task={task}
                      actions={actions}
                      moveTargets={moveTargets}
                      draggable
                      inline
                    />
                  </div>
                </Fragment>
              ))}
              {insertIndex === tasks.length && <InsertionLine />}
            </div>
            {/* 떠 있는 드래그 클론(DragClone=body portal). e.delta 위치 갱신은 handleDragMove 가 담당. */}
            {drag && (
              <DragClone rect={drag.rect} overlayRef={overlayRef}>
                <TaskCard
                  task={drag.task}
                  actions={actions}
                  moveTargets={moveTargets}
                  inline
                  overlay
                />
              </DragClone>
            )}
          </DndContext>
        )
      ) : (
        <div className="flex flex-1 items-center justify-center px-4 py-10 text-center text-sm text-muted-foreground">
          이 날은 예정된 학습이 없어요.
        </div>
      )}

      <DialogFooter className="shrink-0 border-t px-4 py-3 sm:justify-start">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onViewWeek(dateISO)}
        >
          <CalendarRange className="mr-1 h-4 w-4" aria-hidden />이 주 캘린더
          보기
        </Button>
      </DialogFooter>
    </>
  );
}
